import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Observable, Subscription } from 'rxjs';

import { Dietitian } from '../../../../data/dietitian/domain/dietitian';
import { ScheduledTaskEventProvider } from '../../../../data/dietitian/domain/events/scheduled_task/Scheduled_task_event_provider';
import { Notification } from '../../../../data/dietitian/domain/notification/notification';
import { PatientGroup } from '../../../../data/dietitian/domain/patient_group/patient_group';
import { IncomingMessageEventProvider } from '../../../../data/notification/incoming-message-event-provider';
import { SegmentedNavigationItemModel } from '../../components/segmented-navigation/components/segmented-navigation-item/segmented-navigation-item.model';
import { SegmentedNavigationComponent } from '../../components/segmented-navigation/segmented-navigation.component';
import { CreatePatientModalComponent } from './components/create-patient-modal/create-patient-modal.component';
import { QuestionsFacade, QuestionsViewModel } from './questions.facade';
import { QuestionsService, SortListType } from './questions.service';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'msd-questions',
  templateUrl: './questions.component.html',
  styleUrls: ['./questions.component.scss'],
  providers: [QuestionsFacade],
})
export class QuestionsComponent implements OnInit, OnDestroy {
  private dietitian?: Dietitian;
  private questionsStatusSub: Subscription;
  private patientsEventsSubscription?: Subscription;
  private selectedSortListTypeSubscription?: Subscription;
  private dietitianStateProviderSubscription?: Subscription;
  private selectedGroupSubscription?: Subscription;
  private incomingMessageEventSubscription?: Subscription;
  private routerEventsSubscription: Subscription;
  private scheduledTaskEventSubscription: Subscription;

  public bsModalRef?: BsModalRef;
  public questionsStatus$: Observable<QuestionsViewModel>;
  public segmentedItems: SegmentedNavigationItemModel[] = [
    new SegmentedNavigationItemModel('patients', 'Patients'),
    new SegmentedNavigationItemModel('list', 'Messages'),
    new SegmentedNavigationItemModel('diary', 'Journaux'),
    new SegmentedNavigationItemModel(
      'scheduled-task',
      'Tâches programmées',
      undefined,
      undefined,
      undefined,
      true,
    ),
  ];

  @ViewChild('tabs')
  public tabs: SegmentedNavigationComponent;
  public SortListType = SortListType;
  public isLoadingNotifications = false;

  public notificationsError?: string;
  public notifications?: Notification[];
  public isLoadingGroups = false;
  public groupsError?: string;

  public groups: PatientGroup[];

  public group?: PatientGroup;
  public dietName = 'Anonyme';
  public markingAsReadNotification?: Notification;
  public loadingMarkAsReadAll: false;

  constructor(
    private facade: QuestionsFacade,
    private router: Router,
    private modalService: BsModalService,
    private incomingMessageEventProvider: IncomingMessageEventProvider,
    private scheduledTaskEventProvider: ScheduledTaskEventProvider,
    public questionsService: QuestionsService,
  ) {
    this.questionsStatus$ = this.facade.questionsStatus$;
    this.routerEventsSubscription = router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        if (event.url === '/dashboard/questions') {
          this.segmentedItems.forEach((item) => {
            item.isActive = item.id === 'patients';
          });
          if (this.tabs) {
            this.tabs.setActiveItem();
            this.questionsService.loadParams(this.groups).then();
          }
        }
      }
    });
  }

  public ngOnInit() {
    this.incomingMessageEventSubscription =
      this.incomingMessageEventProvider.events$.subscribe(() => {
        this.loadNotifications();
      });

    this.questionsStatusSub = this.questionsStatus$.subscribe(async (state) => {
      this.dietitian = state.dietitian;

      this.dietName = this.dietitian?.firstName ?? this.dietName;
      if (!state.isLoading && state.dietitian) {
        await this.loadNotifications();
        await this.loadGroups();
        await this.questionsService.loadParams(this.groups);
        await this.loadItemsCount(true, true, true);
      }
    });

    this.selectedGroupSubscription =
      this.questionsService.selectedGroupEmitter.subscribe((selectedGroup) => {
        this.group = selectedGroup;
        this.loadItemsCount(true, true, true).then();
      });

    this.selectedSortListTypeSubscription =
      this.questionsService.selectedSortListTypeEmitter.subscribe(
        async (type) => {
          if (type == SortListType.All || type == SortListType.Archived) {
            this.group = undefined;
          }
          await this.loadItemsCount(true, true, true);
        },
      );

    this.scheduledTaskEventSubscription =
      this.scheduledTaskEventProvider.events$.subscribe((event) => {
        if (event.entity) {
          this.loadItemsCount(false, true, false).then();
        }
      });
  }

  public loadNotifications() {
    if (!this.dietitian) {
      return;
    }

    this.isLoadingNotifications = true;

    return this.facade
      .getDietitianNotifications(this.dietitian?.id?.toString())
      .then((notifications) => {
        this.notifications = notifications;
      })
      .catch((error) => {
        this.notificationsError = error;
      })
      .finally(() => {
        this.isLoadingNotifications = false;
      });
  }

  public async loadGroups() {
    if (!this.dietitian) {
      return;
    }

    this.isLoadingGroups = true;

    await this.facade
      .getDietitianPatientGroups(this.dietitian?.id?.toString())
      .then((groups) => {
        this.groups = groups;
      })
      .catch((error) => {
        this.groupsError = error;
      })
      .finally(() => {
        this.isLoadingGroups = false;
      });
  }

  public async loadItemsCount(
    refreshPatient: boolean,
    refreshTask: boolean,
    refreshConversation: boolean,
  ) {
    if (!this.dietitian) {
      return;
    }

    let patientCount = 0;
    let programedAndScheduledCount = 0;

    if (
      this.questionsService.selectedSortListType == SortListType.All &&
      this.dietitian.activatedPatientCount
    ) {
      if (refreshPatient) {
        patientCount = await this.facade.countPatients(
          this.dietitian?.id?.toString(),
        );
      }

      if (refreshTask) {
        programedAndScheduledCount =
          (await this.facade.getDietitianEncouragementCount(
            this.dietitian?.id?.toString(),
          )) +
          (await this.facade.getDietitianScheduledTaskCount(
            this.dietitian?.id?.toString(),
          ));
      }
    } else if (
      this.questionsService.selectedSortListType == SortListType.Archived &&
      this.dietitian.archivedPatientCount
    ) {
      if (refreshPatient) {
        patientCount = await this.facade.countArchivedPatients(
          this.dietitian?.id?.toString(),
        );
      }

      if (refreshTask) {
        programedAndScheduledCount =
          (await this.facade.getDietitianEncouragementCount(
            this.dietitian?.id?.toString(),
          )) +
          (await this.facade.getDietitianScheduledTaskCount(
            this.dietitian?.id?.toString(),
          ));
      }
    } else if (
      this.questionsService.selectedSortListType == SortListType.Group &&
      this.questionsService.selectedGroup?.activatedPatientCount
    ) {
      if (refreshPatient) {
        patientCount =
          this.questionsService.selectedGroup?.activatedPatientCount;
      }

      if (refreshTask) {
        programedAndScheduledCount =
          (await this.facade.getDietitianEncouragementCountByGroup(
            this.dietitian?.id?.toString(),
            this.questionsService.selectedGroup?.id.toString(),
          )) +
          (await this.facade.getDietitianScheduledTaskCountByGroup(
            this.dietitian?.id?.toString(),
            this.questionsService.selectedGroup?.id.toString(),
          ));
      }
    }

    if (refreshPatient) {
      this.segmentedItems[0].itemsCount = patientCount;
    }

    if (refreshTask) {
      this.segmentedItems[3].itemsCount = programedAndScheduledCount;
    }

    if (refreshConversation) {
      this.facade
        .getDietitianConversationCount(
          this.questionsService.selectedSortListType,
          this.group,
        )
        .then((count) => {
          this.segmentedItems[1].itemsCount = count;
        });
    }
  }

  ngOnDestroy(): void {
    this.questionsStatusSub?.unsubscribe();
    this.selectedGroupSubscription?.unsubscribe();
    this.dietitianStateProviderSubscription?.unsubscribe();
    this.selectedSortListTypeSubscription?.unsubscribe();
    this.patientsEventsSubscription?.unsubscribe();
    this.incomingMessageEventSubscription?.unsubscribe();
    this.routerEventsSubscription?.unsubscribe();
    this.scheduledTaskEventSubscription?.unsubscribe();
  }

  updateSortListType(type: SortListType) {
    this.questionsService.updateSortListType(type);
  }

  updatePatientGroup(group: PatientGroup) {
    this.questionsService.updateSelectedGroup(group);
  }

  getSortListTypeName() {
    switch (this.questionsService.selectedSortListType) {
      case SortListType.All:
        return 'Tous les patients';
      case SortListType.Archived:
        return 'Patients archivés';
      case SortListType.Group:
        return 'Groupe ' + this.questionsService.selectedGroup?.name;
      default:
        return 'Tous les patients';
    }
  }

  openPatientCreationModal() {
    const initialState = { class: 'modal-lg' };
    this.bsModalRef = this.modalService.show(
      CreatePatientModalComponent,
      initialState,
    );
  }

  async markAsRead(notification: Notification) {
    if (!this.dietitian) {
      return;
    }

    this.markingAsReadNotification = notification;

    const notificationToRemove = await this.facade.markNotificationAsRead(
      this.dietitian?.dietitianId?.id?.toString(),
      notification,
    );

    if (notificationToRemove) {
      // remove the notification from the list
      this.notifications = this.notifications?.filter(
        (obj) =>
          notificationToRemove.notificationId.id.toString() !=
          obj.notificationId.id.toString(),
      );
    }

    this.markingAsReadNotification = undefined;
  }

  openPatient(payload: { [field: string]: string | null }) {
    this.router.navigate(['/patient', payload['patientId']]).then();
  }

  async markAsReadAll() {
    if (this.notifications && this.notifications.length > 0) {
      for (const notification of this.notifications) {
        await this.markAsRead(notification);
      }
    }
  }
}
